/**
 * Where to send a reader after they sign in: back to the page they came from.
 *
 * The path travels in `?next=` on the sign-in link, and anything in a URL is
 * attacker-controlled: a link to `/signin?next=https://evil.example` must not
 * forward a freshly signed-in reader to another site. So `next` is accepted
 * only as a relative path on this site, and anything else is ignored rather
 * than repaired:
 *
 * - it must start with exactly one `/` (`//evil.example` is another host to a
 *   browser, and so is `/\evil.example`, which browsers read as `//`);
 * - no backslash or control character anywhere, no scheme;
 * - resolved against a placeholder origin, it must stay on that origin;
 * - not an API path, and not one of the sign-in pages themselves, which
 *   would loop.
 *
 * What comes back is the path alone, rebuilt from the parsed URL, never the
 * raw string. The query and the fragment are dropped. Every link the site
 * writes into `?next=` (signInPath) or `?about=` (the report link) carries
 * the path only, so a query that arrives here was written by whoever made the
 * link. Kept, it would travel into the page the reader lands on, and a page's
 * query now reaches the server-rendered meta tags (page-response.ts,
 * describePage). The defence belongs here, on the side that faces the
 * attacker, not only in signInPath.
 */

const AUTH_PAGES = new Set(['/signin', '/register', '/verify', '/forgot-password', '/reset-password'])
const MAX_LENGTH = 512
const PLACEHOLDER = 'https://return-path.invalid'

/** A safe path to return to, or null when `raw` is missing or not one. */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_LENGTH) return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  // Backslashes and control characters, including tab and newline, which
  // browsers strip from URLs before they read them.
  for (const char of raw) {
    const code = char.charCodeAt(0)
    if (char === '\\' || code < 0x20 || code === 0x7f) return null
  }

  let url: URL
  try {
    url = new URL(raw, PLACEHOLDER)
  } catch {
    return null
  }
  if (url.origin !== PLACEHOLDER) return null
  if (url.pathname.startsWith('/api/') || AUTH_PAGES.has(url.pathname)) return null
  return url.pathname
}

/**
 * The sign-in link for a reader on `path`: `/signin?next=…`, or plain
 * `/signin` where no return makes sense.
 *
 * The path only, never its query string: safeReturnPath drops it. The link
 * is written into every page (the server renders it into the HTML), and a
 * query string is whatever the link the reader followed said: carried into
 * `next`, a malformed `?a=` on the quiz would be reflected into the page,
 * which the quiz promises never happens. The reader returns to the page, not
 * to its query.
 */
export function signInPath(path: string): string {
  const next = safeReturnPath(path)
  return next === null || next === '/' ? '/signin' : `/signin?next=${encodeURIComponent(next)}`
}

/**
 * The registration link for a reader on `path`: `/register?next=…`, carrying
 * the page they were on through the whole loop (#105), or plain `/register`
 * where no return makes sense. The same rules as signInPath.
 */
export function registerPath(path: string): string {
  const next = safeReturnPath(path)
  return next === null || next === '/' ? '/register' : `/register?next=${encodeURIComponent(next)}`
}

/**
 * Where the sign-in at the end of registration leads (#105). There is no
 * automatic sign-in after verification, so registration always ends at the
 * sign-in page, and this is the link to it from the "check your email" page
 * and from the verification page.
 *
 * With a page to return to, it carries that page. With none, it carries `/`,
 * the worlds page, where a direct sign-in also ends since 4 Oct (#105): the
 * explicit `next=/` says so at the link rather than relying on the default.
 */
export function signInAfterRegistration(returnTo: string | null): string {
  return `/signin?next=${encodeURIComponent(returnTo ?? '/')}`
}
